import { useState, useEffect } from 'react';
import { getTenantSettings, updateTenantSettings, getAiOptions } from '../../api/tenant';
import { previewAi } from '../../api/ai';
import { getMediaFileUrl } from '../../api/media';
import AlertBanner from '../../components/common/AlertBanner';
import LoadingSpinner from '../../components/common/LoadingSpinner';

function groupByProvider(list) {
  return list.reduce((acc, m) => {
    (acc[m.provider] = acc[m.provider] || []).push(m);
    return acc;
  }, {});
}
const isDefaultPair = (def, prov, model) => def?.provider === prov && def?.model === model;

// Wiederverwendbares Modell-Dropdown (nach Provider gruppiert, Standard markiert)
function ModelSelect({ label, models, value, onChange, defaultPair, hint }) {
  if (!models.length) {
    return <div className="text-muted small">Keine Modelle vom Betreiber freigegeben.</div>;
  }
  return (
    <div>
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {Object.entries(groupByProvider(models)).map(([prov, list]) => (
          <optgroup key={prov} label={prov}>
            {list.map((m) => (
              <option key={`${prov}::${m.model}`} value={`${prov}::${m.model}`}>
                {m.model}{isDefaultPair(defaultPair, prov, m.model) ? ' (Standard)' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {hint && <div className="form-text mt-1">{hint}</div>}
    </div>
  );
}

export default function AiConfigPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [existingConfig, setExistingConfig] = useState({});
  const [aiOptions, setAiOptions] = useState({ text: null, image: null });

  // Text
  const [textSel, setTextSel] = useState('');
  const [aiTextPrompt, setAiTextPrompt] = useState('');
  const [defaultTextPrompt, setDefaultTextPrompt] = useState('');

  // Bild
  const [imageSel, setImageSel] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [defaultPrompt, setDefaultPrompt] = useState('');

  // Vorschau Text
  const [prevTextName, setPrevTextName] = useState('Liegestütze');
  const [prevTextResult, setPrevTextResult] = useState(null);
  const [prevTextLoading, setPrevTextLoading] = useState(false);
  const [prevTextError, setPrevTextError] = useState('');

  // Vorschau Bild
  const [prevImgName, setPrevImgName] = useState('Liegestütze');
  const [prevImgResult, setPrevImgResult] = useState(null);
  const [prevImgLoading, setPrevImgLoading] = useState(false);
  const [prevImgError, setPrevImgError] = useState('');

  const [activeTab, setActiveTab] = useState('text'); // 'text' | 'image'

  useEffect(() => {
    Promise.all([getTenantSettings(), getAiOptions().catch(() => null)])
      .then(([tenantData, optsData]) => {
        const cfg = tenantData?.config || {};
        const opts = optsData || { text: null, image: null };
        setExistingConfig(cfg);
        setAiOptions(opts);

        const imgDef = opts.image?.prompt_default || '';
        setDefaultPrompt(imgDef);
        setAiPrompt(cfg.ai_image_prompt || imgDef);

        const txtDef = opts.text?.prompt_default || '';
        setDefaultTextPrompt(txtDef);
        setAiTextPrompt(cfg.ai_text_prompt || txtDef);

        const pick = (cfgProv, cfgModel, def) => {
          if (cfg[cfgProv] && cfg[cfgModel]) return `${cfg[cfgProv]}::${cfg[cfgModel]}`;
          if (def?.provider && def?.model) return `${def.provider}::${def.model}`;
          return '';
        };
        setTextSel(pick('ai_text_provider', 'ai_text_model', opts.text?.default));
        setImageSel(pick('ai_image_provider', 'ai_image_model', opts.image?.default));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const trimmed = aiPrompt.trim();
      const promptToSave = (!trimmed || trimmed === defaultPrompt.trim()) ? null : trimmed;
      const txtTrimmed = aiTextPrompt.trim();
      const textPromptToSave = (!txtTrimmed || txtTrimmed === defaultTextPrompt.trim()) ? null : txtTrimmed;
      const [textProvider, textModelId] = textSel ? textSel.split('::') : ['', ''];
      const [imageProvider, imageModelId] = imageSel ? imageSel.split('::') : ['', ''];
      const aiFields = {
        ai_image_prompt: promptToSave,
        ai_text_prompt: textPromptToSave,
        ai_text_provider: textProvider || null,
        ai_text_model: textModelId || null,
        ai_image_provider: imageProvider || null,
        ai_image_model: imageModelId || null,
      };
      await updateTenantSettings({ config: { ...existingConfig, ...aiFields } });
      setExistingConfig((prev) => ({ ...prev, ...aiFields }));
      setSuccess('KI-Konfiguration gespeichert.');
    } catch (e) {
      setError(e.message || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  }

  async function runTextPreview() {
    setPrevTextLoading(true);
    setPrevTextError('');
    setPrevTextResult(null);
    try {
      const [provider, model] = textSel ? textSel.split('::') : ['', ''];
      const res = await previewAi({
        use_case: 'text',
        provider,
        model,
        prompt: aiTextPrompt,
        sample_name: prevTextName || 'Liegestütze',
      });
      setPrevTextResult(res);
    } catch (e) {
      setPrevTextError(e.message || 'Vorschau fehlgeschlagen.');
    } finally {
      setPrevTextLoading(false);
    }
  }

  async function runImagePreview() {
    setPrevImgLoading(true);
    setPrevImgError('');
    setPrevImgResult(null);
    try {
      const [provider, model] = imageSel ? imageSel.split('::') : ['', ''];
      const res = await previewAi({
        use_case: 'image',
        provider,
        model,
        prompt: aiPrompt,
        sample_name: prevImgName || 'Liegestütze',
      });
      setPrevImgResult(res);
    } catch (e) {
      setPrevImgError(e.message || 'Vorschau fehlgeschlagen.');
    } finally {
      setPrevImgLoading(false);
    }
  }

  const textPromptMatchesDefault = aiTextPrompt.trim() === defaultTextPrompt.trim();
  const promptMatchesDefault = aiPrompt.trim() === defaultPrompt.trim();
  const promptHasPlaceholder = aiPrompt.includes('{name}');
  const textModels = aiOptions.text?.models || [];
  const imageModels = aiOptions.image?.models || [];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center pb-2 mb-4 border-bottom">
        <h1 className="h2">
          <i className="bi bi-robot me-2" />
          KI-Konfiguration
        </h1>
      </div>

      {error && <AlertBanner message={error} type="danger" onDismiss={() => setError('')} />}
      {success && <AlertBanner message={success} type="success" onDismiss={() => setSuccess('')} />}

      <p className="text-muted">
        Modelle und Prompt-Vorlagen für die KI-gestützte Text- und Bildgenerierung. Die Standard-Prompts
        sind vom Betreiber vorgegeben und hier für deinen Mandanten überschreibbar. Mit „Durchlaufen"
        testest du die gerade eingestellten (noch nicht gespeicherten) Werte.
      </p>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link${activeTab === 'text' ? ' active' : ''}`}
            onClick={() => setActiveTab('text')}
          >
            <i className="bi bi-chat-text me-1" />Textgenerierung
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link${activeTab === 'image' ? ' active' : ''}`}
            onClick={() => setActiveTab('image')}
          >
            <i className="bi bi-image me-1" />Bildgenerierung
          </button>
        </li>
      </ul>

      <form onSubmit={handleSave}>
        <div>
          {/* ── Textgenerierung ── */}
          <div style={{ display: activeTab === 'text' ? 'block' : 'none' }}>
            <div className="card">
              <div className="card-header">
                <i className="bi bi-chat-text me-2" />
                Textgenerierung (Übungsbeschreibungen)
              </div>
              <div className="card-body d-flex flex-column">
                <ModelSelect
                  label="Text-Modell"
                  models={textModels}
                  value={textSel}
                  onChange={setTextSel}
                  defaultPair={aiOptions.text?.default}
                  hint="Wähle das KI-Modell für die Textgenerierung aus der vom Betreiber freigegebenen Liste."
                />

                <div className="mt-4">
                  <label className="form-label fw-semibold">Prompt-Vorlage</label>
                  <textarea
                    className="form-control font-monospace"
                    rows={7}
                    value={aiTextPrompt}
                    onChange={(e) => setAiTextPrompt(e.target.value)}
                    placeholder={defaultTextPrompt}
                  />
                  <div className="form-text mt-1">
                    Platzhalter <code>{'{name}'}</code>, <code>{'{difficulty}'}</code> und{' '}
                    <code>{'{equipment}'}</code> werden ersetzt. Das JSON-Ausgabeformat wird
                    automatisch angehängt.
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setAiTextPrompt(defaultTextPrompt)}
                      disabled={textPromptMatchesDefault}
                    >
                      <i className="bi bi-arrow-counterclockwise me-1" />
                      Auf Standard zurücksetzen
                    </button>
                    {textPromptMatchesDefault && (
                      <span className="text-muted small ms-2">(entspricht dem globalen Standard)</span>
                    )}
                  </div>
                </div>

                {/* Vorschau Text */}
                <div className="mt-auto pt-4">
                  <div className="fw-semibold small mb-2">
                    <i className="bi bi-play-circle me-1" />
                    Beispiel durchlaufen
                  </div>
                  <div className="input-group input-group-sm mb-2">
                    <span className="input-group-text">Übung</span>
                    <input
                      type="text"
                      className="form-control"
                      value={prevTextName}
                      onChange={(e) => setPrevTextName(e.target.value)}
                      placeholder="Liegestütze"
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={runTextPreview}
                      disabled={prevTextLoading || !textSel}
                    >
                      {prevTextLoading ? (
                        <><span className="spinner-border spinner-border-sm me-1" />Läuft…</>
                      ) : (
                        <><i className="bi bi-lightning-charge me-1" />Durchlaufen</>
                      )}
                    </button>
                  </div>
                  {prevTextError && (
                    <div className="alert alert-danger py-2 small mb-0">
                      <i className="bi bi-exclamation-triangle me-1" />
                      {prevTextError}
                    </div>
                  )}
                  {prevTextResult && (
                    <div className="border rounded p-3 small" style={{ background: 'var(--color-surface-2)' }}>
                      <div className="text-muted mb-1">
                        {prevTextResult.provider} · {prevTextResult.model}
                      </div>
                      <div className="fw-semibold">Beschreibung</div>
                      <div className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{prevTextResult.description}</div>
                      <div className="fw-semibold">Anleitung</div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{prevTextResult.instructions}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Bildgenerierung ── */}
          <div style={{ display: activeTab === 'image' ? 'block' : 'none' }}>
            <div className="card">
              <div className="card-header">
                <i className="bi bi-image me-2" />
                Bildgenerierung (Übungs-Illustrationen)
              </div>
              <div className="card-body d-flex flex-column">
                <ModelSelect
                  label="Bild-Modell"
                  models={imageModels}
                  value={imageSel}
                  onChange={setImageSel}
                  defaultPair={aiOptions.image?.default}
                  hint="Wähle das KI-Modell für die Bildgenerierung aus der vom Betreiber freigegebenen Liste."
                />

                <div className="mt-4">
                  <label className="form-label fw-semibold">Prompt-Vorlage</label>
                  <textarea
                    className="form-control font-monospace"
                    rows={7}
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={defaultPrompt}
                  />
                  <div className="form-text mt-1">
                    Der Platzhalter <code>{'{name}'}</code> wird durch den Übungsnamen ersetzt.
                  </div>
                  {aiPrompt && !promptHasPlaceholder && (
                    <div className="alert alert-warning mt-2 py-2 mb-0 small">
                      <i className="bi bi-exclamation-triangle me-1" />
                      Der Prompt enthält keinen <code>{'{name}'}</code>-Platzhalter — der Übungsname
                      wird nicht eingesetzt.
                    </div>
                  )}
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setAiPrompt(defaultPrompt)}
                      disabled={promptMatchesDefault}
                    >
                      <i className="bi bi-arrow-counterclockwise me-1" />
                      Auf Standard zurücksetzen
                    </button>
                    {promptMatchesDefault && (
                      <span className="text-muted small ms-2">(entspricht dem globalen Standard)</span>
                    )}
                  </div>
                </div>

                {/* Vorschau Bild */}
                <div className="mt-auto pt-4">
                  <div className="fw-semibold small mb-2">
                    <i className="bi bi-play-circle me-1" />
                    Beispiel durchlaufen
                  </div>
                  <div className="input-group input-group-sm mb-2">
                    <span className="input-group-text">Übung</span>
                    <input
                      type="text"
                      className="form-control"
                      value={prevImgName}
                      onChange={(e) => setPrevImgName(e.target.value)}
                      placeholder="Liegestütze"
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={runImagePreview}
                      disabled={prevImgLoading || !imageSel}
                    >
                      {prevImgLoading ? (
                        <><span className="spinner-border spinner-border-sm me-1" />Läuft…</>
                      ) : (
                        <><i className="bi bi-lightning-charge me-1" />Durchlaufen</>
                      )}
                    </button>
                  </div>
                  <div className="form-text mb-2">
                    <i className="bi bi-info-circle me-1" />
                    Erzeugt ein echtes, kostenpflichtiges Bild und speichert es in die Medien.
                  </div>
                  {prevImgError && (
                    <div className="alert alert-danger py-2 small mb-0">
                      <i className="bi bi-exclamation-triangle me-1" />
                      {prevImgError}
                    </div>
                  )}
                  {prevImgResult && (
                    <div className="border rounded p-2 text-center" style={{ background: 'var(--color-surface-2)' }}>
                      <div className="text-muted small mb-2">
                        {prevImgResult.provider} · {prevImgResult.model}
                      </div>
                      <img
                        src={getMediaFileUrl(prevImgResult.media_id)}
                        alt="KI-Vorschau"
                        className="img-fluid rounded"
                        style={{ maxHeight: 320 }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <><span className="spinner-border spinner-border-sm me-2" />Speichern…</>
            ) : (
              <><i className="bi bi-floppy me-1" />Speichern</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
